import type { RedFlagsBlock as RedFlagsBlockType, RedFlagItem } from '../types';
import { BlockEmptyState } from './BlockEmptyState';

const severityRailColorMap: Record<RedFlagItem['severity'], string> = {
  critical: 'bg-[#991B1B]',
  high: 'bg-[#F97316]',
  medium: 'bg-[#F59E0B]',
};

// ─────────────────────────────────────────────
// RedFlagsBlock — 风险红线清单
// Figma: node 10426-25575
// 每条 risk row = 左侧 4px 深红竖线(rail) + 右侧内容区(标题 + evidence + source)
// 容器：bg-[#F8F8F7] rounded-[12px] padding 12px 14px
// ─────────────────────────────────────────────

export function RedFlagsBlock({ block }: { block: RedFlagsBlockType }) {
  const items = block.items ?? [];
  // 兼容旧版 flags 字段（不在标准类型中，做类型断言访问）
  const flags = ((block as unknown as Record<string, unknown>).flags as { title: string; description: string }[]) ?? [];

  if (items.length === 0 && flags.length === 0) return <BlockEmptyState label="风险红线" />;

  // 新版 items 渲染
  if (items.length > 0) {
    return (
      <div className="flex flex-col gap-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex flex-row gap-3 bg-[#F8F8F7] rounded-[12px] px-[14px] py-3">
            {/* rail — 左侧竖线，颜色随 severity 变化 */}
            <div
              className={`w-1 shrink-0 self-stretch rounded-full ${severityRailColorMap[item.severity ?? 'critical']}`}
            />
            {/* copy — 右侧内容区 */}
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              {/* 标题 */}
              <span className="text-base font-medium leading-[150%] text-[#12231A]">{item.label}</span>
              {/* evidence + source */}
              <div className="flex flex-col gap-[5px]">
                <p className="text-sm font-normal leading-[150%] tracking-[0.07px] text-[#262626]">{item.evidence}</p>
                {item.source_hint && (
                  <span className="text-sm font-normal leading-[150%] tracking-[0.07px] text-[#737373]">
                    Source: {item.source_hint}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 旧版 flags 兼容渲染
  return (
    <div className="flex flex-col gap-2">
      {flags.map((flag, idx) => (
        <div key={idx} className="flex flex-row gap-3 bg-[#F8F8F7] rounded-[12px] px-[14px] py-3">
          <div className="w-1 shrink-0 self-stretch rounded-full bg-[#991B1B]" />
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <span className="text-base font-medium leading-[150%] text-[#12231A]">{flag.title}</span>
            {flag.description && (
              <p className="text-sm font-normal leading-[150%] tracking-[0.07px] text-[#262626]">{flag.description}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
